import React, { useState } from "react";
import axios from "axios";
import { X, ShoppingBag } from "lucide-react";
import { SHOP } from "@/constants/testIds";
import { useCart } from "@/context/CartContext";
import { gstBreakup, r2 } from "@/lib/whatsapp";
import CartStage from "./cart/CartStage";
import CheckoutStage from "./cart/CheckoutStage";
import SuccessStage from "./cart/SuccessStage";
import { useBackClose } from "@/lib/backstack";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stageTitle = (stage) =>
  stage === "success" ? "Order Placed" : stage === "checkout" ? "Checkout" : "Your Cart";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, mrpTotal, clearCart, setLineDiscount } = useCart();
  const [stage, setStage] = useState("cart"); // cart | checkout | success
  const [form, setForm] = useState({ customer_name: "", phone: "", address: "", pincode: "" });
  const [placedOrder, setPlacedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [includeGst, setIncludeGst] = useState(true);
  const [includeDelivery, setIncludeDelivery] = useState(true);
  const [deliveryFeeInput, setDeliveryFeeInput] = useState(null);

  const { base: baseSubtotal, gst } = gstBreakup(subtotal);
  const defaultDeliveryFee = subtotal >= 500 || subtotal === 0 ? 0 : 49;
  const deliveryFeeValue = deliveryFeeInput ?? defaultDeliveryFee;
  const deliveryFee = includeDelivery ? deliveryFeeValue : 0;
  const productsTotal = baseSubtotal + (includeGst ? gst : 0);
  const total = productsTotal + deliveryFee;
  const savings = mrpTotal - subtotal;

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => { setStage("cart"); setErr(""); }, 200);
  };
  useBackClose(open, handleClose);

  const placeOrder = async () => {
    setErr("");
    if (!form.customer_name || !form.phone || !form.address || !form.pincode) {
      setErr("Please fill all fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        ...form,
        include_gst: includeGst,
        delivery_fee: deliveryFee,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          qty: i.qty,
          variant_id: i.variantInfo?.variant_id,
          selections: i.variantInfo?.selections,
        })),
      });
      setPlacedOrder(res.data);
      setStage("success");
      clearCart();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        onClick={handleClose}
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />

      <aside
        data-testid={SHOP.cartDrawer}
        className={`fixed top-0 right-0 h-full w-full sm:w-[460px] bg-white z-50 shadow-2xl transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} className="text-[#0056D2]" />
            <h3 className="font-display font-extrabold text-lg text-gray-900">{stageTitle(stage)}</h3>
          </div>
          <button onClick={handleClose} className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {stage === "cart" && (
          <CartStage
            items={items}
            updateQty={updateQty}
            removeItem={removeItem}
            setLineDiscount={setLineDiscount}
            subtotal={subtotal}
            baseSubtotal={baseSubtotal}
            gst={gst}
            includeGst={includeGst}
            setIncludeGst={setIncludeGst}
            includeDelivery={includeDelivery}
            setIncludeDelivery={setIncludeDelivery}
            deliveryFeeValue={deliveryFeeValue}
            setDeliveryFee={setDeliveryFeeInput}
            productsTotal={productsTotal}
            deliveryFee={deliveryFee}
            total={total}
            savings={savings}
            onCheckout={() => setStage("checkout")}
          />
        )}

        {stage === "checkout" && (
          <CheckoutStage
            form={form}
            setForm={setForm}
            baseSubtotal={baseSubtotal}
            gst={gst}
            includeGst={includeGst}
            deliveryFee={deliveryFee}
            total={total}
            err={err}
            submitting={submitting}
            onPlaceOrder={placeOrder}
            onBack={() => setStage("cart")}
          />
        )}

        {stage === "success" && placedOrder && (
          <SuccessStage order={placedOrder} onClose={handleClose} />
        )}
      </aside>
    </>
  );
}
